import { DatePipe } from "@angular/common";
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Inject,
    ViewChild,
} from "@angular/core";

import { PodcastDeprecated } from "@hermes/api-model-editorial";

import { CHAPTERS_PARAGRAPH } from "../../core";

import { PodcastAnalyticsService } from "./podcast-analytics.service";

@Component({
    selector: "h-podcast",
    templateUrl: "./podcast.component.html",
    styleUrls: ["./podcast.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class PodcastComponent implements AfterViewInit {
    @ViewChild("progressbar")
    public inputRange!: ElementRef;

    @ViewChild("audioElement")
    public audioReference!: ElementRef<HTMLAudioElement>;

    public totalTime = this.timeconverter(this.podcast.omnyData.duration);
    public openSubscribe: boolean = false;
    public firstPlay = true;
    public lastPercentageSendToAnalytics = 0;

    public audio!: HTMLAudioElement;

    constructor(
        @Inject(CHAPTERS_PARAGRAPH) public podcast: PodcastDeprecated,
        private cd: ChangeDetectorRef,
        private datePipe: DatePipe,
        public podcastAnalyticsService: PodcastAnalyticsService,
    ) {}

    public get paused(): boolean {
        if (this.audio) {
            return this.audio.paused;
        }
        return true;
    }

    public pause(): void {
        if (this.audio) {
            this.audio.pause();
            this.podcastAnalyticsService.sendPauseEvent(
                this.podcast.omnyData.title,
            );
        }
    }

    public timeconverter(timetoConvert: number = 0): string {
        return this.datePipe.transform(
            new Date(timetoConvert * 1000),
            "mm:ss",
        ) as string;
    }

    public play(): void {
        if (this.podcast.omnyData && this.audio) {
            this.audio.play();

            if (this.firstPlay) {
                this.firstPlay = false;
                this.podcastAnalyticsService.sendFirstPlayEvent(
                    this.podcast.omnyData.title,
                );
            } else {
                this.podcastAnalyticsService.sendNewPlayEvent(
                    this.podcast.omnyData.title,
                );
            }
        }
    }

    public changeTime(value: string): void {
        this.audio.pause();
        this.audio.currentTime = Number.parseFloat(value);
    }

    public moveRwFw(rwfw: "rewind" | "forward"): void {
        if (rwfw === "rewind") {
            this.audio.currentTime -= 15;
            this.podcastAnalyticsService.sendRewindEvent(
                this.podcast.omnyData.title,
            );
        } else if (rwfw === "forward") {
            this.audio.currentTime += 15;
            this.podcastAnalyticsService.sendForwardEvent(
                this.podcast.omnyData.title,
            );
        }
    }

    public muteAudio(): void {
        this.audio.muted = !this.audio.muted;
        this.podcastAnalyticsService.sendMuteAudioEvent(
            this.podcast.omnyData.title,
        );
    }

    public setProgressBarAndAnalytics(): void {
        const currentProgress =
            this.audio.currentTime / this.podcast.omnyData.duration;
        const currentPercentageProgress = Math.trunc(currentProgress * 100);
        const listOfPercentageAnalytics = [25, 50, 75, 95];
        this.inputRange.nativeElement.style.backgroundImage = `-webkit-gradient(linear, left top, right top, color-stop(${currentProgress}, #3c3c3c),color-stop(${currentProgress}, #f6f1eb)`;

        this.sendAnalytics(
            currentPercentageProgress,
            listOfPercentageAnalytics,
        );
    }
    public sendAnalytics(
        currentPercentageProgress: number,
        listOfPercentageAnalytics: number[],
    ): void {
        if (
            Math.trunc(this.audio.currentTime) ===
                Math.trunc(this.podcast.omnyData.duration) &&
            this.lastPercentageSendToAnalytics !== currentPercentageProgress
        ) {
            this.lastPercentageSendToAnalytics = currentPercentageProgress;
            this.podcastAnalyticsService.sendEndOfPodcastEvent(
                this.podcast.omnyData.title,
            );
        }

        if (listOfPercentageAnalytics.includes(currentPercentageProgress)) {
            this.sendProgressPodcastAnalytics(currentPercentageProgress);
        }
    }

    public sendProgressPodcastAnalytics(realTimePercentage: number): void {
        if (this.lastPercentageSendToAnalytics !== realTimePercentage) {
            this.lastPercentageSendToAnalytics = realTimePercentage;
            this.podcastAnalyticsService.sendPercentageProgressPodcastEvent(
                this.podcast.omnyData.title,
                `${this.lastPercentageSendToAnalytics}%`,
            );
        }
    }

    public toggleSubscription(): void {
        this.openSubscribe = !this.openSubscribe;
        this.podcastAnalyticsService.sendSubscribeEvent(
            this.podcast.omnyData.title,
        );
    }

    public subscribeService(platformName: string): void {
        this.podcastAnalyticsService.sendSubscribePlatformEvent(
            platformName,
            this.podcast.omnyData.title,
        );
    }

    public ngAfterViewInit(): void {
        this.audio = this.audioReference.nativeElement;
        this.cd.detectChanges();
        this.audio.addEventListener("timeupdate", () => {
            this.totalTime =
                this.podcast.omnyData.duration - this.audio.currentTime <= 0
                    ? this.timeconverter(this.podcast.omnyData.duration)
                    : `-${this.timeconverter(
                          this.podcast.omnyData.duration -
                              this.audio.currentTime,
                      )}`;

            this.setProgressBarAndAnalytics();
            this.cd.detectChanges();
        });
    }
}
